// src/modules/access/AccessHubPage.tsx
// Página Central de Acesso - Admin e Tenants

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { useAuth, ADMIN_UIDS } from '../auth/AuthContext';
import { 
  AccessButton, 
  AccessHubConfig, 
  getIconComponent,
  adminAccessButtons 
} from './accessConfig';
import { getAdminAccessHub, getTenantAccessHub } from './accessService';

interface AccessHubPageProps {
  mode?: 'admin' | 'tenant';
  tenantId?: string;
  tenantType?: string;
}

export function AccessHubPage({ 
  mode = 'admin', 
  tenantId, 
  tenantType = 'pousada' 
}: AccessHubPageProps) {
  const [config, setConfig] = useState<AccessHubConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { tenant, user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && user) {
      if (ADMIN_UIDS.includes(user.uid)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/admin/profile');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    async function loadConfig() {
      setLoading(true);
      try {
        if (mode === 'admin') {
          const adminConfig = await getAdminAccessHub();
          setConfig(adminConfig);
        } else if (tenantId) {
          const tenantConfig = await getTenantAccessHub(tenantId, tenantType);
          setConfig(tenantConfig);
        } else {
          // Fallback para config padrão
          setConfig({
            buttons: adminAccessButtons,
            customized: false,
          });
        }
      } catch (error) {
        console.error('Erro ao carregar config:', error);
        setConfig({
          buttons: adminAccessButtons,
          customized: false,
        });
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, [mode, tenantId, tenantType]);

  const handleButtonClick = (button: AccessButton) => {
    if (button.path.startsWith('http')) {
      window.open(button.path, '_blank');
    } else {
      navigate(button.path);
    }
  };

  const visibleButtons = config?.buttons.filter(btn => btn.visible) || [];

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-glow-mesh-dark opacity-30" />
      
      {/* Back Link */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 flex items-center gap-2 text-white/40 hover:text-white transition-colors text-sm font-bold z-10"
      >
        <ArrowLeft size={18} />
        Voltar ao Portal
      </Link>

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-brand-charcoal/30 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden relative z-10 backdrop-blur-3xl"
      >
        {/* Header */}
        <div className="p-10 text-center border-b border-white/5 bg-white/[0.02]">
          <div className="flex justify-center mb-6">
            <Logo width={160} />
          </div>
          <h1 className="text-3xl font-black text-white font-display uppercase tracking-tight">
            Central de Acesso
          </h1>
          <p className="text-white/40 mt-2 text-xs font-black uppercase tracking-widest">
            {mode === 'admin' ? 'Gestão Empresarial e Acesso' : tenant?.businessName || 'Estabelecimento'}
          </p>
        </div>

        {/* Buttons Grid */}
        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-brand-gold" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleButtons.map((button, index) => {
                const IconComponent = getIconComponent(button.icon);
                return (
                  <motion.button
                    key={button.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleButtonClick(button)}
                    className="group p-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl text-left transition-all duration-300"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-${button.color || 'brand-gold'}/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent 
                        size={24} 
                        className={`text-${button.color || 'brand-gold'} opacity-70 group-hover:opacity-100 transition-opacity`} 
                      />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-brand-gold transition-colors">
                      {button.label}
                    </h3>
                    <p className="text-sm text-white/40 font-medium">
                      {button.description}
                    </p>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 text-center bg-white/[0.02] border-t border-white/5">
          <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em]">
            Powered by <span className="text-white/40">Nossa Pesca Engine</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default AccessHubPage;
